import { RichTextEditor, type TiptapEditor } from "@base/ui/RichTextEditor";
import { Button, Chip } from "@buildblockinc/buildblock-design-system";
import { MILESTONE_REPORT_CHIPS } from "@features/write-daily-log/model/constants";
import type { DailyLogForm } from "@features/write-daily-log/model/form";
import { useAIRefine } from "@features/write-daily-log/model/useAIRefine";
import { RefineLoadingOverlay } from "@features/write-daily-log/ui/RefineLoadingOverlay";
import RefineWithAIButton from "@features/write-daily-log/ui/RefineWithAIButton";
import { useParams } from "@tanstack/react-router";
import { Lightbulb, RotateCcw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

const DESCRIPTION_MAX_LENGTH = 5000;

interface MilestoneReportStepProps {
  form: DailyLogForm;
}

export function MilestoneReportStep({ form }: MilestoneReportStepProps) {
  const { projectId } = useParams({ strict: false });
  const { refine, isRefining, isSuccess, isFailure, description } = useAIRefine(projectId!);
  const editorRef = useRef<TiptapEditor | null>(null);
  const previousHtmlRef = useRef<string | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [textLength, setTextLength] = useState(0);

  useEffect(() => {
    if (isSuccess && description && editorRef.current) {
      editorRef.current.commands.setContent(description);
      setCanUndo(previousHtmlRef.current !== null);
    }
  }, [isSuccess, description]);

  useEffect(() => {
    if (isFailure) {
      toast.error("AI refinement failed. Please try again.");
    }
  }, [isFailure]);

  const handleRefine = () => {
    previousHtmlRef.current = editorRef.current?.getHTML() ?? null;
    setCanUndo(false);
    refine(editorRef.current?.getHTML() ?? "");
  };

  const handleUndo = () => {
    if (previousHtmlRef.current && editorRef.current) {
      editorRef.current.commands.setContent(previousHtmlRef.current);
      previousHtmlRef.current = null;
      setCanUndo(false);
    }
  };

  return (
    <>
      <div className="mb-4 space-y-3">
        <div className="flex gap-x-2 items-center">
          <Lightbulb width={20} height={20} className="text-gray-1000" />
          <p className="text-gray-1000 text-subtitle4">Please record the following:</p>
        </div>

        <div className="flex gap-[6px] flex-wrap">
          {MILESTONE_REPORT_CHIPS.map((chip) => (
            <Chip key={chip.id} color="default" size="md" radius="square">
              <span className="text-gray-900 text-label3">{chip.name}</span>
            </Chip>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <form.Field name="description">
          {(field) => (
            <div className="flex flex-col gap-y-1">
              <p className="text-gray-900 text-label2">
                Description <span className="text-red-500">*</span>
              </p>
              <div className="relative">
                <RichTextEditor
                  value={field.state.value}
                  onChange={(html) => {
                    field.handleChange(html);
                    const text = editorRef.current?.getText() ?? "";
                    setTextLength(text.length);
                  }}
                  onBlur={field.handleBlur}
                  onEditorReady={(editor) => {
                    editorRef.current = editor;
                    setTextLength(editor.getText().length);
                  }}
                  placeholder="Type in today's report"
                  disabled={isRefining}
                />
                {isRefining && <RefineLoadingOverlay />}
              </div>
              <p className="text-right text-gray-500 text-label3">
                {textLength} / {DESCRIPTION_MAX_LENGTH}
              </p>
            </div>
          )}
        </form.Field>

        <div className="flex gap-2">
          <RefineWithAIButton
            label={isRefining ? "Refining..." : "Refine with AI"}
            onClick={handleRefine}
            disabled={isRefining || editorRef.current?.isEmpty}
            className="flex-1"
          />

          <Button
            variant="outline"
            color="secondary"
            onClick={handleUndo}
            disabled={!canUndo}
            prefixIcon={<RotateCcw />}
          >
            Undo
          </Button>
        </div>
      </div>
    </>
  );
}
